import React, { useState } from 'react';
import { Phone } from 'lucide-react';
import Modal from '../../../ui-kit/Modal';
import Button from '../../../ui-kit/Button';
import { useChat } from '../../chat/state/ChatContext';
import { auth } from '../../../integrations/firebase';
import { CallActivityService } from '../../../core/customer/CallActivityService';
import { recordTimelineEvent } from '../../../core/customer/CustomerTimelineEngine';
import dialViaPhone from '../../../core/telephony/dialViaPhone';

const RESULTS = [
    'Connected', 'Not Answered', 'Busy', 'Switched Off', 'Wrong Number', 'Call Later',
    'Interested', 'Not Interested', 'Ordered', 'Complaint Raised',
];

// Dial + log: replaces silent `tel:` links in operational modules so every call
// lands in the unified callActivities stream (Telecaller Workspace). Pass the
// context (sourceModule, callType, orderId, ticketId) for the calling module.
const QuickCallButton = ({
    mobile, customerName = '', orderId = '', ticketId = '',
    sourceModule = 'manual', callType = '', label, className, title = 'Call & log', iconSize = 16,
    onBeforeDial, onLogged,
}) => {
    const { currentUser } = useChat();
    const [open, setOpen] = useState(false);
    const [result, setResult] = useState('Connected');
    const [remark, setRemark] = useState('');
    const [followUp, setFollowUp] = useState('');
    const [saving, setSaving] = useState(false);
    const [phoneDial, setPhoneDial] = useState(null);   // null | 'sending' | 'sent' | error string
    const [sessionId, setSessionId] = useState('');
    const [conflict, setConflict] = useState(false);
    const [phoneConfirmed, setPhoneConfirmed] = useState(false);

    const dial = async () => {
        // Optional caller guard (e.g. over-call gap / DND). Sync OR async —
        // an async guard can fetch the one customer record it needs.
        if (typeof onBeforeDial === 'function') {
            const ok = await Promise.resolve(onBeforeDial());
            if (ok === false) return;
        }
        // Coordinated call start: the backend checks permission, assignment,
        // DND/cooldown and takes the atomic reservation before the phone
        // dials. A conflict ("on call with X") disables duplicate calling.
        // Goes through the one shared dial path, so this button also gets the
        // phone-offline check and the live "did the phone get it" confirmation.
        setPhoneDial('sending'); setConflict(false); setSessionId(''); setPhoneConfirmed(false); setOpen(true);
        try {
            const res = await dialViaPhone(mobile, callType || 'manual');
            if (res.ok) { setSessionId(res.sessionId || ''); setPhoneDial('sent'); setPhoneConfirmed(!!res.confirmed); }
            else { setPhoneDial(res.error || 'Could not reach your phone'); setConflict(!!res.blocked); }
        } catch (e) { setPhoneDial('Could not reach your phone'); }
    };

    const save = async () => {
        if (result === 'Call Later' && !followUp) {
            alert('"Call Later" needs the date & time you promised to call back.');
            return;
        }
        setSaving(true);
        try {
            const by = currentUser?.name || currentUser?.displayName || currentUser?.email
                || auth.currentUser?.displayName || auth.currentUser?.email || 'Agent';
            const followUpMs = followUp ? new Date(followUp).getTime() : 0;
            await CallActivityService.logCall({
                mobile, customerName, orderId, ticketId, sourceModule, callType,
                result, remark,
                // When the call went out through the phone app, the app's OS
                // event is the attempt — this record is the business outcome.
                outcomeOnly: phoneDial === 'sent', sessionId,
                calledBy: by, calledByUid: auth.currentUser?.uid || '', calledByRole: currentUser?.role || '',
                followUpAt: followUpMs || undefined,
            });
            if (mobile) {
                await recordTimelineEvent(mobile, 'call_made', {
                    note: `${callType || 'Call'} — ${result}${remark ? `: ${remark}` : ''}`,
                    status: result, source: sourceModule,
                }, by);
            }
            setOpen(false); setRemark(''); setFollowUp(''); setResult('Connected');
            onLogged?.();
        } catch (e) { alert(e.message || 'Failed to log call'); }
        finally { setSaving(false); }
    };

    const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-100';

    return (
        <>
            <button type="button" onClick={dial} title={title}
                className={className || 'p-1.5 rounded hover:bg-green-50 text-green-600'}>
                {label || <Phone size={iconSize} />}
            </button>
            {open && (
                <Modal isOpen={open} onClose={() => setOpen(false)} title="Log Call Result" className="max-w-sm">
                    <div className="space-y-3">
                        {phoneDial && (
                            <div className={`text-xs rounded-lg px-3 py-2 flex items-center justify-between gap-2 ${
                                phoneDial === 'sent' ? 'bg-green-50 text-green-700'
                                : phoneDial === 'sending' ? 'bg-slate-50 text-slate-500'
                                : 'bg-amber-50 text-amber-700'}`}>
                                <span>
                                    {phoneDial === 'sending' ? '📲 Sending to your phone…'
                                        : phoneDial === 'sent' ? (phoneConfirmed ? '📲 Your phone confirmed — it is dialling now.' : '📲 Sent to your phone — waiting for it to confirm.')
                                        : `⚠ ${phoneDial}`}
                                </span>
                                <span className="shrink-0 text-slate-400">rings via Daksha Caller</span>
                            </div>
                        )}
                        <div className="text-sm text-slate-500">
                            {customerName ? <b className="text-slate-700">{customerName}</b> : null} {mobile}
                            {callType ? <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-slate-100">{callType}</span> : null}
                        </div>
                        <label className="block text-xs font-medium text-slate-500">Result
                            <select className={inputCls} value={result} onChange={e => setResult(e.target.value)}>
                                {RESULTS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </label>
                        <label className="block text-xs font-medium text-slate-500">Remark
                            <textarea className={inputCls} rows={2} value={remark} onChange={e => setRemark(e.target.value)} placeholder="What happened on the call?" />
                        </label>
                        <label className="block text-xs font-medium text-slate-500">Follow-up (optional)
                            <input type="datetime-local" className={inputCls} value={followUp} onChange={e => setFollowUp(e.target.value)} />
                        </label>
                        <div className="flex justify-between items-center pt-1">
                            <button onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:text-slate-700">Close without logging</button>
                            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Call Log'}</Button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default QuickCallButton;
